import { useState, useEffect, useContext } from "react";
import {
  TariffTile,
  TileSlider,
  EqualHeight,
  List,
  Icon,
  Price,
  Tooltip,
  Asterisk,
  CopyText,
  TextLink,
  Modal,
  Button,
} from "@dtpk-cc/components";
import ActionSuccessSelected from "@dtpk-cc/components/dist/icons/Action/Success/Selected";

import "@dtpk-cc/components/dist/global.css";
import "@dtpk-cc/components/dist/components/TariffTile/TariffTile.css";
import "@dtpk-cc/components/dist/components/Tile/Tile.css";
import "@dtpk-cc/components/dist/components/List/List.css";
import "@dtpk-cc/components/dist/components/CopyText/CopyText.css";
import "@dtpk-cc/components/dist/components/Heading/Heading.css";
import "@dtpk-cc/components/dist/components/Price/Price.css";
import "@dtpk-cc/components/dist/components/HeroFeature/HeroFeature.css";
import "@dtpk-cc/components/dist/components/TileSlider/TileSlider.css";
import "@dtpk-cc/components/dist/components/Icon/Icon.css";
import "@dtpk-cc/components/dist/components/SliderButton/SliderButton.css";
import "@dtpk-cc/components/dist/components/SliderIndicator/SliderIndicator.css";
import "@dtpk-cc/components/dist/components/Dot/Dot.css";
import "@dtpk-cc/components/dist/components/Tooltip/Tooltip.css";
import "@dtpk-cc/components/dist/components/TextLink/TextLink.css";
import "@dtpk-cc/components/dist/components/Asterisk/Asterisk.css";
import "@dtpk-cc/components/dist/components/Modal/Modal.css";
import "@dtpk-cc/components/dist/components/Button/Button.css";

import AvailabilityCheck from "../../components/AvailabilityCheck";
import TariffDetails from "../../components/TariffDetails";
import { ModalContext } from "../../contexts/ModalContext";

import styles from "./TariffTiles.module.css";

import tariffData from "../../assets/tariff-data.json";

function TariffTiles() {
  const [tariffTiles, setTariffTiles] = useState([]);
  const [tkgTariff, setTkgTariff] = useState(null);

  const [cashbackExists, setCashbackExists] = useState(false);

  const [showTariffDetails, setShowTariffDetails] = useState(null);
  const [showDeviceSelection, setShowDeviceSelection] = useState(false);

  const resetTariffTiles = () => {
    const tariffs = ["mzs", "mzm", "mzl", "mzxl"].map(
      (kenner) => tariffData[kenner]
    );
    setTariffTiles(tariffs);
    setCashbackExists(tariffs.some((tariff) => tariff.cashback.value !== null));
    setTkgTariff(null);

    // Ein Versuch das Problem mit dem EqualHight Element zu lösen,
    // welches manchmal die Höhen direkt nach dem Laden der Seite
    // falsch berechnet
    setTimeout(() => {
      try {
        window.dispatchEvent(new Event("resize"));
      } catch (e) {
        /* */
      }
    }, 300);
  };

  const updateTariffTiles = (data) => {
    let searchForS = data.empfehlungen.empfehlung.find((tariff) => tariff.kenner.includes("mzs"))
    let searchForM = data.empfehlungen.empfehlung.find((tariff) => tariff.kenner.includes("mzm"))
    setTkgTariff(
     (searchForM && !searchForS) ? searchForM : null
    );
    const tariffs = data.empfehlungen.empfehlung
      .map((item) => {
        const { kenner } = item;

        if (kenner in tariffData) {
          return tariffData[kenner];
        }

        return null;
      })
      .filter((item) => item !== null);

    setTariffTiles(tariffs);
    setCashbackExists(tariffs.some((tariff) => tariff.cashback.value !== null));
  };

  const { setIsModalOpen, setFnId } = useContext(ModalContext);

  const openModal = (fnId) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFnId(fnId);
    setIsModalOpen(true);
  };

  const selectTariff = (kenner) => {
    if (kenner === "mzxl") {
      setShowDeviceSelection(true);
      return;
    }

    document.getElementById(`tariff-form-${kenner}`).submit();
  };

  useEffect(() => {
    resetTariffTiles();
  }, []);

  const renderTariffTile = (tariff) => {
    return (
      <TariffTile
        buttonText="Tarif auswählen"
        customClass="tariff-tile"
        onClick={() => selectTariff(tariff.kenner)}
      >
        <TariffTile.Name name={tariff.productName} />
        <TariffTile.Hero value={tariff.maxDownload} suffix="MBit/s" />
        <TariffTile.Description description={`max. Download`} />
        <TariffTile.Description
          description={`und ${tariff.maxUpload} MBit/s max. Upload`}
        />

        <div className={styles["usp-list"]}>
          <List
            icon={{
              icon: ActionSuccessSelected,
              color: Icon.Color.functionGreen,
            }}
          >
            {tariff.usps.map(renderUsp)}
          </List>
        </div>

        <div className={styles["price-wrapper"]}>
          <TariffTile.Price
            value={tariff.price.discountValue}
            suffix="mtl."
            textAfterPrice={`ab dem ${
              tariff.price.discountDuration + 1
            }. Monat ${tariff.price.defaultValue} €`}
          />
          <matrix-asterisk variants={[styles["price-footnote"]]} matrix-id={tariff.footnoteId}></matrix-asterisk>
        </div>

        {cashbackExists && (
          <Price.Text variants={Price.Text.Variant.benefit}>
            {tariff.cashback.value === null ? (
              <span>&nbsp;</span>
            ) : (
              <span>
                {tariff.cashback.value} € Gutschrift{" "}
                <Asterisk onClick={openModal(tariff.cashback.footnoteId)}>
                  Fußnote
                </Asterisk>
                <matrix-asterisk matrix-id={tariff.cashback.footnoteId}></matrix-asterisk>
              </span>
            )}
          </Price.Text>
        )}

        <List variants={List.Variant.noBullets}>
          <List.Item>
            <List.Text>
              <CopyText tag="div" variants={CopyText.Variant.additional}>
                <TextLink
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    setShowTariffDetails(tariff.kenner);
                  }}
                >
                  Tarifdetails
                </TextLink>
              </CopyText>
            </List.Text>
          </List.Item>
          <List.Item>
            <List.Text>
              <CopyText tag="div" variants={CopyText.Variant.additional}>
                <TextLink href={tariff.productInformationUrl}>
                  Produktinformationsblatt
                </TextLink>
              </CopyText>
            </List.Text>
          </List.Item>
        </List>

        <form
          action="https://www.telekom.de/is-bin/INTERSHOP.enfinity/WFS/EKI-PK-Site/-/-/-/ViewDSE-Start"
          method="post"
          acceptCharset="ISO-8859-1"
          onSubmit={() => (document.charset = "ISO-8859-1")}
          id={`tariff-form-${tariff.kenner}`}
        >
          <input type="hidden" name="Mode" value="ProduktIndividuell" />
          <input
            type="hidden"
            name="ProductRefID"
            value={`${tariff.sku}@EKI-PK`}
          />
          <input
            type="hidden"
            name="Geschaeftsfall"
            value="BESTANDSKUNDE_AENDERN"
          />
          <input type="hidden" name="AutoContinue" value="true" />
          <input type="hidden" name="vpnr" value="3240022" />
        </form>

        {tariff.kenner === "mzxl" && renderDeviceSelectionModal(tariff)}
      </TariffTile>
    );
  };

  const renderUsp = (usp) => {
    return (
      <List.Item key={usp.text}>
        <List.Text>
          <Tooltip content={<>{usp.tooltip}</>}>{usp.text}</Tooltip>
        </List.Text>
      </List.Item>
    );
  };

  const renderDeviceSelectionModal = (tariff) => {
    return (
      <Modal
        isOpen={!!showDeviceSelection}
        afterClose={() => {
          setShowDeviceSelection(false);
        }}
        zIndex={99999}
      >
        <Modal.Section>
          <Modal.Heading>
            Sehr geehrter Kunde,
            <br />
            für die Nutzung des Angebots benötigen Sie einen der folgenden
            Router:
          </Modal.Heading>
          <div className={styles["device-list"]}>
            <List>
              <List.Item>
                <List.Text>Speedport PRO</List.Text>
              </List.Item>
              <List.Item>
                <List.Text>Speedport Smart 2</List.Text>
              </List.Item>
              <List.Item>
                <List.Text>Speedport Smart 3</List.Text>
              </List.Item>
              <List.Item>
                <List.Text>Speedport Smart 3 S</List.Text>
              </List.Item>
              <List.Item>
                <List.Text>Speedport Smart S</List.Text>
              </List.Item>
              <List.Item>
                <List.Text>Speedport W925V</List.Text>
              </List.Item>

              <List.Item>
                <List.Text>AVM FRITZ!BOX 7590</List.Text>
              </List.Item>
              <List.Item>
                <List.Text>AVM FRITZ!BOX 7590 S</List.Text>
              </List.Item>
              <List.Item>
                <List.Text>AVM FRITZ!BOX 7590@GK</List.Text>
              </List.Item>
            </List>
          </div>
          <div className={styles["device-selection"]}>
            <form
              action="https://www.telekom.de/is-bin/INTERSHOP.enfinity/WFS/EKI-PK-Site/-/-/-/ViewDSE-Start"
              method="post"
              acceptCharset="ISO-8859-1"
              onSubmit={() => (document.charset = "ISO-8859-1")}
            >
              <input type="hidden" name="Mode" value="ProduktIndividuell" />
              <input
                type="hidden"
                name="ProductRefID"
                value={`${tariff.sku}@EKI-PK`}
              />
              <input
                type="hidden"
                name="Geschaeftsfall"
                value="BESTANDSKUNDE_AENDERN"
              />
              <input type="hidden" name="AutoContinue" value="true" />
              <input type="hidden" name="vpnr" value="3240022" />
              <input
                data-v-270f5531=""
                type="hidden"
                name="goto"
                value="basket1"
              ></input>
              <Button variants={[Button.Variant.outline]} type="submit">
                Ich besitze einen der genannten Router
              </Button>
            </form>

            <form
              action="https://www.telekom.de/is-bin/INTERSHOP.enfinity/WFS/EKI-PK-Site/-/-/-/ViewDSE-Start"
              method="post"
              acceptCharset="ISO-8859-1"
              onSubmit={() => (document.charset = "ISO-8859-1")}
            >
              <input type="hidden" name="Mode" value="ProduktIndividuell" />
              <input
                type="hidden"
                name="ProductRefID"
                value={`${tariff.sku}@EKI-PK`}
              />
              <input
                type="hidden"
                name="Geschaeftsfall"
                value="BESTANDSKUNDE_AENDERN"
              />
              <input type="hidden" name="AutoContinue" value="true" />
              <input type="hidden" name="vpnr" value="3240022" />
              <input
                data-v-270f5531=""
                type="hidden"
                name="goto"
                value="ipds"
              ></input>
              <Button type="submit">
                Ich besitze keinen der genannten Router
              </Button>
            </form>
          </div>
        </Modal.Section>
      </Modal>
    );
  };

  const renderTkgInfoText = (tkg) => {
    return (
      <div className={styles.tkgInfoContainer}>
        <CopyText>
          Mit{" "}
          <a href={tkg ? 'https://www.telekom.de//zuhause/tarife-und-optionen/internet/magenta-zuhause-m-12' : "https://www.telekom.de/zuhause/tarife-und-optionen/internet/magenta-zuhause-xs"}>
             {tkg ? tkg.produktName+ ' 12' : "MagentaZuhause XS"}
          </a>{" "}
          mit bis zu {tkg ? '50' : '16'} MBit/s bieten wir auch einen DSL-Tarif für Internet und Telefonie im Festnetz
          mit einer Mindestvertragslaufzeit von 12 Monaten an.<br/> Ein Wechsel in
          diesen Tarif ist zum Ende Ihrer aktuellen Mindestvertragslaufzeit
          möglich.
        </CopyText>
      </div>
    );
  };

  return (
    <div className={styles.container} id="verfuegbar">
      <AvailabilityCheck
        onAvailabilityCheckSuccess={updateTariffTiles}
        onAvailabilityCheckReset={resetTariffTiles}
      />
      <div className={styles["tariff-tiles"]}>
        <div className={styles["tariff-slider-wrapper"]}>
          <EqualHeight>
            {tariffTiles.length > 0 && (
              <TileSlider variant="narrow">
                {tariffTiles.map(renderTariffTile)}
              </TileSlider>
            )}
          </EqualHeight>
          { 
            tariffTiles.length > 0 && (
              renderTkgInfoText(tkgTariff)
            )
          }
        </div>
      </div>

      <Modal
        isOpen={!!showTariffDetails}
        afterClose={() => setShowTariffDetails(false)}
        zIndex={99999}
      >
        <Modal.Section>
          <TariffDetails tariff={tariffData[showTariffDetails]} />
        </Modal.Section>
      </Modal>
    </div>
  );
}

export default TariffTiles;
